import { ColumnType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router-dom";
import { projectsApis } from "../../api/projects";
import dayjs from "dayjs";
import { tasksApis } from "../../api/NewTasks";
import { Progress, Tooltip } from "antd";

const TaskCompletionReport = () => {
  const [statusFilterData, setStatusFilterData] = useState([]);
  const [ownerFilterData, setOwnerFilterData] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: projectId, projectName } = location.state || {};

  // Scroll to top when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const columns: ColumnType<any>[] = [
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 180,
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`Task-${record?.taskId}`}
              onClick={() => {
                navigate(`/pms/project/task-detail/${record.taskId}`, {
                  state: {
                    projectId,
                    projectName,
                    returnPath: location.pathname,
                    returnState: location.state,
                  },
                });
              }}
            >
              {text}
            </span>
          </>
        );
      },
    },
    {
      title: "Assigned To",
      dataIndex: "taskOwnerName",
      key: "taskOwnerName",
      width: 150,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Planned Start",
      dataIndex: "plannedStartDate",
      key: "plannedStartDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Planned End",
      dataIndex: "plannedEndDate",
      key: "plannedEndDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Actual Start",
      dataIndex: "actualStartDate",
      key: "actualStartDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Actual End",
      dataIndex: "actualEndDate",
      key: "actualEndDate",
      width: 100,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Planned Hours",
      dataIndex: "plannedHours",
      key: "plannedHours",
      width: 120,
      render: (text) => {
        if (text === null || text === undefined || text === "") {
          return "N/A";
        }
        return text;
      },
    },
    {
      title: "Logged Hours",
      dataIndex: "loggedHours",
      key: "loggedHours",
      width: 120,
      render: (text) => {
        if (text === null || text === undefined || text === "") {
          return "N/A";
        }
        return text;
      },
    },
    {
      title: "Completed Percentage",
      dataIndex: "taskCompletionPercentage",
      key: "taskCompletionPercentage",
      width: 200,
      render: (text, record) => {
        if (text === null || text === undefined || text === "") return "N/A";

        const percentage = parseFloat(text.toString().replace("%", ""));

        return (
          <Tooltip title={`Completion: ${text}`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percentage}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Material Consumption",
      dataIndex: "materialConsumption",
      key: "materialConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.materialPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Machinery Consumption",
      dataIndex: "machineryConsumption",
      key: "machineryConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.machineryPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Manpower Consumption",
      dataIndex: "manpowerConsumption",
      key: "manpowerConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.manpowerPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Total Consumption",
      dataIndex: "totalConsumption",
      key: "totalConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.totalPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Delay",
      dataIndex: "delayDays",
      key: "delayDays",
      width: 100,
      render: (text) =>
        text === null || text === undefined || text === ""
          ? "N/A"
          : `${text} Days`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
  ];

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule(
        "TaskCompletionReport"
      );
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchOwnerFilterData = async () => {
    try {
      const response = await tasksApis.getOwnersFilterData(
        projectId?.toString()
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
          };
        });
        setOwnerFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    fetchStatusFilterData();
    fetchOwnerFilterData();
    dispatch(
      updateMenuData([
        {
          text: "Task Level Progress Summary Report",
          link: "/pms/task-completion-report",
        },
      ])
    );
  }, []);
  return (
    <>
      <Template1
        moduleKey={window.location.pathname}
        columns={columns}
        pageTitle="Task Level Progress Summary Report"
        pageDescription=""
        previousPath={{
          link: location.state?.activityIds
            ? "/pms/activity-level-progress-summary-report"
            : "/pms/project/reports",
          state: location.state?.activityIds
            ? {
                id: location.state.id,
                projectName: location.state.projectName,
                phaseIds: location.state.phaseIds,
                phaseName: location.state.phaseName,
                // Clear activityIds when going back to activity level to show all activities in that phase
              }
            : {
                id: location.state?.id,
                projectName: location.state?.projectName,
              },
        }}
        filters={{
          defaultFilters: {
            dateRange: {
              title: "Custom Date Range",
            },
          },
          customFilters: [
            {
              type: "multiple_selection",
              name: "phaseStatus",
              label: "Status",
              options: statusFilterData,
            },
            {
              type: "multiple_selection",
              name: "UserId",
              label: "Users",
              options: ownerFilterData,
            },
          ],
        }}
      />
    </>
  );
};

export default TaskCompletionReport;
